/**
 * KST(Asia/Seoul, UTC+9) 기준 날짜 포맷 유틸리티
 *
 * 서버는 KST 기준 datetime을 timezone offset 없이 내려줍니다 (예: "2024-01-15T10:30:00").
 * JavaScript의 new Date()는 offset 없는 ISO 문자열을 로컬 시간으로 해석하는데,
 * UTC 환경(EC2 등)에서는 KST 값을 UTC로 잘못 파싱합니다.
 * parseKSTDate()를 사용하면 offset 없는 문자열에 +09:00을 붙여 올바르게 파싱합니다.
 */

const KST_TZ = 'Asia/Seoul';

/**
 * 서버에서 내려온 datetime 문자열을 KST 기준으로 파싱합니다.
 * - offset 없는 문자열 ("2024-01-15T10:30:00") → KST로 간주하여 +09:00 추가
 * - Z 또는 +HH:MM offset이 있는 문자열 → 그대로 파싱
 */
export const parseKSTDate = (dateStr: string): Date => {
  if (!dateStr) return new Date(NaN);
  // 이미 timezone 정보가 있으면 그대로 파싱
  if (/Z$/i.test(dateStr) || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr);
  }
  // offset 없으면 KST(+09:00)으로 간주
  return new Date(dateStr + '+09:00');
};

/** ISO datetime 문자열 → "YYYY.MM.DD" (KST 기준) */
export const formatDateKST = (dateStr: string): string => {
  const d = parseKSTDate(dateStr);
  const parts = new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: KST_TZ,
  }).formatToParts(d);
  const year = parts.find(p => p.type === 'year')?.value ?? '';
  const month = parts.find(p => p.type === 'month')?.value ?? '';
  const day = parts.find(p => p.type === 'day')?.value ?? '';
  return `${year}.${month}.${day}`;
};

/** ISO datetime 문자열 → "YYYY.MM.DD HH:MM" (KST 기준) */
export const formatDateTimeKST = (dateStr: string): string => {
  const d = parseKSTDate(dateStr);
  const parts = new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: KST_TZ,
  }).formatToParts(d);
  const year = parts.find(p => p.type === 'year')?.value ?? '';
  const month = parts.find(p => p.type === 'month')?.value ?? '';
  const day = parts.find(p => p.type === 'day')?.value ?? '';
  const hour = parts.find(p => p.type === 'hour')?.value ?? '';
  const minute = parts.find(p => p.type === 'minute')?.value ?? '';
  return `${year}.${month}.${day} ${hour}:${minute}`;
};

/** Date 객체 → "HH:MM" (KST 기준) — MessageBubble 등 시간만 표시할 때 사용 */
export const formatTimeKST = (date: Date): string => {
  return date.toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: KST_TZ,
  });
};

/**
 * ISO datetime 문자열 → 상대적 또는 절대 시간 (KST 기준)
 * 메시지 리스트의 마지막 메시지 시간 표시에 사용
 */
export const formatTimestampKST = (dateString: string | null): string => {
  if (!dateString) return '';
  const date = parseKSTDate(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: KST_TZ,
    });
  } else if (days === 1) {
    return '어제';
  } else if (days < 7) {
    return `${days}일 전`;
  } else {
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      timeZone: KST_TZ,
    });
  }
};
